'use client'

import './Toast.css'
import { Button, ButtonTypes, ButtonVariants, Spinner, Text } from '../../../atoms'
import { CloseIcon, InfoNoIcon, InfoSuccessIcon } from '../../../icons'
import { DictionaryContext } from '../../../../providers'
import { ToastVariant, type ToastOptions } from '../../../../store/toastStore'
import React, { useContext, useEffect, useRef } from 'react'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../../constants/iconButtonAriaLabelMessageKeys'

export enum ToastAriaRole {
    STATUS = 'status',
    ALERT = 'alert',
}

export enum ToastAriaLive {
    POLITE = 'polite',
    ASSERTIVE = 'assertive',
}

const variantAriaRole: Record<ToastVariant, ToastAriaRole> = {
    [ToastVariant.LOADING]: ToastAriaRole.STATUS,
    [ToastVariant.SUCCESS]: ToastAriaRole.STATUS,
    [ToastVariant.ERROR]: ToastAriaRole.ALERT,
}

const variantAriaLive: Record<ToastVariant, ToastAriaLive> = {
    [ToastVariant.LOADING]: ToastAriaLive.POLITE,
    [ToastVariant.SUCCESS]: ToastAriaLive.POLITE,
    [ToastVariant.ERROR]: ToastAriaLive.ASSERTIVE,
}

const ToastIcon = ({ variant }: { variant?: ToastVariant }): React.ReactNode => {
    switch (variant) {
        case ToastVariant.LOADING:
            return <Spinner />
        case ToastVariant.ERROR:
            return <InfoNoIcon />
        case ToastVariant.SUCCESS:
            return <InfoSuccessIcon />
        default:
            return null
    }
}

export interface ToastProps extends ToastOptions {
    onRemove?: () => void
    isLeaving?: boolean
    onLeaveComplete?: () => void
}

export const Toast = ({
    message,
    variant,
    actionText,
    onAction,
    onRemove,
    isLeaving = false,
    onLeaveComplete,
}: ToastProps): React.ReactNode | null => {
    const dictionary = useContext(DictionaryContext)

    if (!message) return null

    const loading = variant === ToastVariant.LOADING
    const role = variant ? variantAriaRole[variant] : ToastAriaRole.STATUS
    const ariaLive = variant ? variantAriaLive[variant] : ToastAriaLive.POLITE
    const animationClass = isLeaving ? 'toast-leave' : 'toast-enter'
    const leaveCompleteCalled = useRef(false)

    useEffect(() => {
        if (!isLeaving || !onLeaveComplete) return
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (reducedMotion) {
            leaveCompleteCalled.current = true
            onLeaveComplete()
        }
    }, [isLeaving, onLeaveComplete])

    const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>): void => {
        if (e.animationName?.includes('toast-leave') && onLeaveComplete && !leaveCompleteCalled.current) {
            leaveCompleteCalled.current = true
            onLeaveComplete()
        }
    }

    return (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[72px] z-50">
            <div
                role={role}
                aria-live={ariaLive}
                aria-atomic
                aria-label={message}
                className={`flex justify-between min-h-[3rem] max-w-xl p-0 contrast bg-primary text-text-default border border-shade-opacity-200 shadow-drop-lg ${animationClass}`}
                onAnimationEnd={handleAnimationEnd}
            >
                <div className="px-4 py-3 flex items-center">
                    {variant && (
                        <span className="w-6 mr-2 flex" aria-hidden>
                            <ToastIcon variant={variant} />
                        </span>
                    )}
                    <Text className="line-clamp-2">{message}</Text>
                </div>

                {actionText != null && onAction != null && (
                    <Button
                        type={ButtonTypes.BUTTON}
                        data-testid="btn-toast-action"
                        variant={ButtonVariants.TERTIARY}
                        className="px-4 py-3"
                        onClick={onAction}
                    >
                        {actionText}
                    </Button>
                )}

                {!loading && onRemove != null && (
                    <div className="border-l border-shade-600 flex items-center">
                        <Button
                            type={ButtonTypes.BUTTON}
                            data-testid="btn-toast-close"
                            variant={ButtonVariants.TERTIARY}
                            className="px-4 py-[12px]"
                            onClick={onRemove}
                            icon={<CloseIcon />}
                            aria-label={resolveIconButtonAriaLabel(
                                dictionary,
                                ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.CLOSE_TOAST,
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
